import { useQuery } from '@apollo/client';
import { AxesProps, ChartProps, Dataset, Record, Series, c4adsPalette, usePalette } from '@c4ads/c4blocks';
import { Icon, IconButton, Menu, MenuItem } from '@mui/material';
import { flatMap, sortBy, startCase } from 'lodash';
import { DateTime } from 'luxon';
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { GET_LOOKUPS } from '../../api/apollo/query';
import { useAuth } from '../../api/auth';
import { svgToImageSrc } from '../../helpers/chartHelpers';
import DashboardContext from '../../routes/Dashboards/Dashboard/DashboardContext';
import errorImgSrc from '../../static/img/error.png';
import noDataImgSrc from '../../static/img/no_data.png';
import { ChartConfig } from '../../types/ChartConfig';
import { DependentVariable } from '../../types/DependentVariable';
import { DiscreteVariable } from '../../types/DiscreteVariable';
import { ChartTextSize } from '../Chart';
import { FullScreenLoader } from '../Loader/FullScreenLoader';
import { useDownloadChart } from './utils/useDownloadChart';


export interface ChartPropProviderProps {
    config: ChartConfig;
    loading: boolean;
    dataset?: Dataset;
    error?: any;
    children: (args: { chartProps: ChartProps }) => ReactNode | null;
}

const ChartPropProvider = ({ config, loading: propsLoading, dataset, error, children }: ChartPropProviderProps) => {
    const { id, x, y, groupby, xLines, yLines, textSize = ChartTextSize.Small } = config;
    const { user } = useAuth();
    const { data: lookups } = useQuery(GET_LOOKUPS);
    const ref = useRef<HTMLDivElement>(null);
    const { handleEditConfig, handleRemoveChart, onExport } = useContext(DashboardContext);
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);

    const { sequentialPalette } = usePalette(c4adsPalette);

    useEffect(() => {
        return onExport(async () => {
            if (!ref.current) {
                return;
            }
            const svgElement = ref.current.querySelector('svg');
            if (!svgElement) {
                return;
            }

            const result = await svgToImageSrc(svgElement);
            if (!result) {
                return;
            }
            const { src, width, height } = result;

            return {
                size: { width, height },
                src,
                title: dataset?.title,
            };
        });
    }, [ref.current]);

    const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        setMenuAnchorEl(e.currentTarget);
    }

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
    }

    const formatAxisLabel = useCallback(
        (x: DiscreteVariable) => {
            let label = x as string;
            if (x === 'ADMINISTRATIVE_LEVEL_1') {
                label = user?.organization?.country?.administrativeLevelNames[1] || 'District';
            }
            return startCase(label.toLowerCase());
        },
        [user]
    );

    const formatLeftAxisLabel = useCallback(
        (y: DependentVariable) => {
            let label = startCase(y.toLowerCase());
            if (label === 'Fine' || label === 'Suspended Fine') {
                label = label.replace('Fine', `Fine (${lookups?.lookups?.currency})`);
            }
            if (label === 'Seizure Weight') {
                label = label.replace('Seizure Weight', `Seizure Weight (kg)`);
            }
            if (label === 'Seizure Value') {
                label = label.replace('Seizure Value', `Seizure Value (${lookups?.lookups?.currency})`);
            }

            return label;
        },
        [lookups]
    );

    const axes = {
        bottom: {
            label: formatAxisLabel(x),
            interval: ['MONTH', 'QUARTER', 'YEAR'].includes(x) ? x.toLowerCase() : null,
            isDate: ['MONTH', 'QUARTER', 'YEAR'].includes(x),
        },
        left: {
            label: formatLeftAxisLabel(y),
        },
    } as AxesProps;

    const palette = ['REGION', 'ADMINISTRATIVE_LEVEL_1'].includes(x)
        ? sequentialPalette('#d2dbe6', 'blue', 10)
        : [c4adsPalette['blue']];

    const data = useMemo(() => {
        let formatted: Series[] = [];

        if (y.endsWith('_RATE')) {
            if (dataset?.data) {
                formatted = dataset.data.map((d: Series) => {
                    return {
                        ...d,
                        data: d.data.map((dd: Record) => {
                            return { ...dd, data: dd.data * 100 };
                        }),
                    };
                });
            }
        } else {
            formatted = dataset?.data || [];
        }

        if (x === 'SPECIES') {
            formatted = formatted.map((d) => ({
                ...d,
                data: sortBy(
                    d.data.map((dd) => ({ ...dd, label: dd.label === '' ? 'Unknown' : dd.label })),
                    'label'
                ),
            }));
        }

        return formatted;
    }, [dataset, x, y, groupby]);

    const serializedChartData = useMemo(() => {
        if (!data.length) return [];

        return data.flatMap((series) =>
            series.data.map(({ label, data, key }) => ({
                series: series.label,
                seriesKey: series.key,
                label,
                data,
                key,
            }))
        );
    }, [data]);
    const fallbackFileName = `chart-data-${new Date().toISOString().replace(/[:.]/g, '-')}`;
    const fileName = dataset?.title || fallbackFileName;
    const { downloadAsXLS, downloadAsCSV, downloadAsImage, isLoading: isDownloadingFile } = useDownloadChart({ data: serializedChartData, fileName });

    const domainY = useMemo(() => {
        if (y.endsWith('_RATE')) {
            return [0, 100];
        }

        if (dataset?.markOptions && dataset.markOptions[0] === 'MultiBar') {
            const allValues = flatMap(dataset.data, (dd) => dd.data).map((d) => d.data);
            const min = Math.min(0, Math.min(...allValues));
            const max = Math.max(...allValues);
            return [min, max];
        }
    }, [y, dataset?.markOptions]);

    const legendValues = useMemo(() => {
        switch (groupby) {
            case 'MONTH':
                return ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
            case 'QUARTER':
                return ['01', '04', '07', '10'];
            default:
                return undefined;
        }
    }, [groupby]);

    const formatLegendLabel = useMemo(() => {
        switch (groupby) {
            case 'MONTH':
                return (label: string) => DateTime.fromISO(`2000-${label}-01`).toFormat('LLL');
            case 'QUARTER':
                return (label: string) => {
                    return `Q${DateTime.fromISO(`2000-${label}-01`).toFormat('q')}`;
                };
            case 'YEAR':
                return (label: string) => DateTime.fromISO(label).toFormat('yyyy');
            default:
                return undefined;
        }
    }, [groupby]);

    const groupAccessor = useMemo(() => {
        switch (groupby) {
            case 'MONTH':
            case 'QUARTER':
                return (key: string) => key.split('-')[1];
            default:
                return undefined;
        }
    }, [groupby]);

    const actions = useMemo(() => {
        const newActions = [
            <IconButton
                key="edit-settings"
                size="small"
                color="inherit"
                onClick={(e) => {
                    e.stopPropagation();
                    handleEditConfig(config);
                }}
            >
                <Icon fontSize="inherit" color="inherit">
                    settings
                </Icon>
            </IconButton>,
        ];
        if (user?.permissions?.map(({ codename }) => codename).includes('can_export_charts')) {
            newActions.push(
                <React.Fragment key="export-menu">
                    <IconButton
                        key="export"
                        size="small"
                        color="inherit"
                        aria-controls={menuOpen ? 'download-menu' : undefined}
                        aria-haspopup="true"
                        aria-expanded={menuOpen ? 'true' : undefined}
                        onClick={handleOpenMenu}
                    >
                        <Icon fontSize="inherit" color="inherit">
                            download
                        </Icon>
                    </IconButton>
                    <Menu id="download-menu" open={menuOpen} anchorEl={menuAnchorEl} onClose={handleCloseMenu}>
                        <MenuItem onClick={() => downloadAsImage({ref, mark: config.mark})}>Download as image</MenuItem>
                        <MenuItem onClick={downloadAsXLS}>Download as xls</MenuItem>
                        <MenuItem onClick={downloadAsCSV}>Download as csv</MenuItem>
                    </Menu>
                </React.Fragment>
            );
        }
        newActions.push(
            <IconButton
                key="close"
                size="small"
                color="inherit"
                onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveChart(id);
                }}
            >
                <Icon fontSize="inherit">close</Icon>
            </IconButton>
        );

        return newActions;
    }, [user, config, menuOpen]);


    const chartProps = {
        title: config.customTitle || dataset?.title,
        description: dataset?.description,
        markOptions: dataset?.markOptions,
        data,
        error,
        errorImgSrc: errorImgSrc,
        noDataImgSrc: noDataImgSrc,
        actions: actions,
        activeMarkIndex: 0,
        width: 400,
        height: 300,
        palette: palette,
        axes: axes,
        domainY: domainY,
        legendValues: legendValues,
        xLines,
        yLines,
        textSize,
        formatLegendLabel: formatLegendLabel,
        groupAccessor: groupAccessor,
    } as ChartProps;


    return (
        <div ref={ref} style={{ height: '100%' }}>
            {isDownloadingFile || (propsLoading && <FullScreenLoader />)}
            {!propsLoading &&
                children({
                    chartProps: chartProps,
                })}
        </div>
    );
};

export default ChartPropProvider;
